import React, { useState, useEffect } from 'react';
import api, { applications as applicationsAPI } from '../../utils/api';
import AdminLayout from './AdminLayout';
import './ApplicationsList.css';

const openDoc = async (storedPath) => {
  if (!storedPath) return;
  try {
    const res = await api.get(
      `/admin/view-file?path=${encodeURIComponent(storedPath)}`,
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(res);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    alert('Document not available or not yet uploaded.');
  }
};

const DocRow = ({ label, path, extra }) => (
  <div className="doc-row">
    <span className={`doc-indicator ${path ? 'doc-indicator--ok' : 'doc-indicator--missing'}`} />
    <span className="doc-label">{label}{extra ? <em> ({extra})</em> : ''}</span>
    {path
      ? <button className="btn-doc-view" onClick={() => openDoc(path)}>View 👁</button>
      : <span className="doc-missing">Not uploaded</span>
    }
  </div>
);

const ApplicationsList = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'All Active' },
    { value: 'pending', label: 'Pending' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'suspended', label: 'Suspended' }
  ];

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [applications, searchTerm, statusFilter]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const data = await applicationsAPI.getAll();
      // Backend returns { applications: [...], total, page, limit }
      setApplications(Array.isArray(data) ? data : (data?.applications || []));
      setError(null);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = [...applications];

    // Always exclude approved applications — they live in the Instructors tab
    if (statusFilter === 'all') {
      filtered = filtered.filter(app => app.status !== 'approved');
    } else {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app =>
        app.full_name?.toLowerCase().includes(term) ||
        app.first_name?.toLowerCase().includes(term) ||
        app.last_name?.toLowerCase().includes(term) ||
        app.email?.toLowerCase().includes(term) ||
        app.phone?.toLowerCase().includes(term) ||
        app.contact_number?.toLowerCase().includes(term)
      );
    }

    setFilteredApplications(filtered);
  };

  const viewDetails = async (appId) => {
    try {
      const res = await applicationsAPI.getOne(appId);
      // Backend returns { application, documents, testCentres, referral }
      const app = res?.application || res;
      app._documents  = res?.documents  || [];
      app._testCentres = res?.testCentres || [];
      app._referral   = res?.referral   || null;
      setSelectedApp(app);
      setShowModal(true);
    } catch (err) {
      console.error('Error fetching application details:', err);
      alert('Failed to load application details');
    }
  };

  const updateStatus = async (appId, newStatus, adminNotes = '', rejectionReason = '') => {
    try {
      setUpdating(true);
      await applicationsAPI.updateStatus(appId, newStatus, adminNotes, rejectionReason);
      await fetchApplications();
      setShowModal(false);
      setSelectedApp(null);
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update application status');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout pageTitle="Instructor Requests">
        <div className="applications-list">
          <div className="loading-container">
            <div className="spinner-large"></div>
            <p>Loading applications...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Instructor Requests">
    <div className="applications-list">

      {error && (
        <div className="alert alert-error">
          ⚠️ {error}
        </div>
      )}

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          {statusOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="results-count">
          {filteredApplications.length} of {applications.length} applications
        </div>

        <button className="ap-refresh-btn" onClick={fetchApplications} disabled={loading} title="Refresh">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transition: 'transform 0.4s', transform: loading ? 'rotate(360deg)' : 'none' }}>
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="table-container">
        <table className="applications-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredApplications.length > 0 ? (
              filteredApplications.map((app) => (
                <tr key={app.id}>
                  <td>#{app.application_number || app.id}</td>
                  <td>
                    <strong>{app.full_name || `${app.first_name || ''} ${app.last_name || ''}`.trim()}</strong>
                  </td>
                  <td>{app.email}</td>
                  <td>{app.contact_number || app.phone}</td>
                  <td>
                    <span className={`status-badge ${app.status}`}>
                      {app.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>{formatDate(app.created_at)}</td>
                  <td>
                    <button
                      onClick={() => viewDetails(app.id)}
                      className="btn-view"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-data">
                  No applications found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && selectedApp && (
        <ApplicationDetailModal
          app={selectedApp}
          onClose={() => {
            setShowModal(false);
            setSelectedApp(null);
          }}
          onUpdateStatus={updateStatus}
          updating={updating}
        />
      )}
    </div>
    </AdminLayout>
  );
};

const ApplicationDetailModal = ({ app, onClose, onUpdateStatus, updating }) => {
  const [newStatus, setNewStatus] = useState(app.status);
  const [adminNotes, setAdminNotes] = useState(app.admin_notes || '');
  const [rejectionReason, setRejectionReason] = useState(app.rejection_reason || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newStatus === 'rejected' && !rejectionReason.trim()) {
      alert('Please enter a rejection reason — it will be emailed to the applicant.');
      return;
    }
    onUpdateStatus(app.id, newStatus, adminNotes, rejectionReason);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Application Details - #{app.application_number || app.id}</h2>
          <button onClick={onClose} className="btn-close">✕</button>
        </div>

        <div className="modal-body">
          <div className="detail-section">
            <h3>Personal Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Full Name:</label>
                <span>{app.full_name}</span>
              </div>
              <div className="detail-item">
                <label>Date of Birth:</label>
                <span>{app.date_of_birth ? new Date(app.date_of_birth).toLocaleDateString('en-GB') : '—'}</span>
              </div>
              <div className="detail-item">
                <label>National Insurance:</label>
                <span>{app.ni_number && app.ni_number !== 'NOT-PROVIDED' ? app.ni_number : '—'}</span>
              </div>
              <div className="detail-item">
                <label>Email:</label>
                <span>{app.email}</span>
              </div>
              <div className="detail-item">
                <label>Phone:</label>
                <span>{app.contact_number}</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Address</h3>
            <p>
              {app.address_line1}<br />
              {app.address_line2 && <>{app.address_line2}<br /></>}
              {app.city}<br />
              {app.county}<br />
              {app.postcode}
            </p>
          </div>

          <div className="detail-section">
            <h3>Driving Licence</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Licence Number:</label>
                <span>{app.licence_number && app.licence_number !== 'NOT-PROVIDED' ? app.licence_number : '—'}</span>
              </div>
              <div className="detail-item">
                <label>PRN:</label>
                <span>{app.prn || '—'}</span>
              </div>
              <div className="detail-item">
                <label>DVLA Check Code:</label>
                <span>{app.dvla_check_code || '—'}</span>
              </div>
            </div>
          </div>

          {app.adi_part1_status && (
            <div className="detail-section">
              <h3>ADI Qualifications</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Part 1:</label>
                  <span className={`badge ${app.adi_part1_status}`}>{app.adi_part1_status}</span>
                </div>
                <div className="detail-item">
                  <label>Part 2:</label>
                  <span className={`badge ${app.adi_part2_status}`}>{app.adi_part2_status}</span>
                </div>
                <div className="detail-item">
                  <label>Part 3:</label>
                  <span className={`badge ${app.adi_part3_status}`}>{app.adi_part3_status}</span>
                </div>
              </div>
            </div>
          )}

          {app._testCentres?.length > 0 && (
            <div className="detail-section">
              <h3>Test Centres</h3>
              <p>{app._testCentres.map(tc => tc.name).join(', ')}</p>
            </div>
          )}

          {(app.was_referred || app._referral) && (
            <div className="detail-section">
              <h3>Referral Information</h3>
              <div className="detail-grid">
                {app.referring_instructor_name && (
                  <div className="detail-item">
                    <label>Referred by:</label>
                    <span>{app.referring_instructor_name} ({app.referring_instructor_adi})</span>
                  </div>
                )}
                {app.referral_code && (
                  <div className="detail-item">
                    <label>Referral Code:</label>
                    <span>{app.referral_code}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="detail-section">
            <h3>📎 Uploaded Documents</h3>
            {app._documents?.length > 0 ? (
              (() => {
                // Group by type for tidy display
                const groups = {
                  identity: ['green_badge_front','green_badge_back','driving_licence_front','driving_licence_back','passport','dbs_barcode','utility_bill','bank_statement'],
                  vehicle:  ['insurance_doc','mot_certificate','vehicle_exterior_photos','vehicle_interior_photos','dual_controls_photos','l_plates'],
                  other:    []
                };
                const grouped = { identity: [], vehicle: [], other: [] };
                app._documents.forEach(doc => {
                  const key = doc.document_type;
                  if (groups.identity.some(k => key.startsWith(k))) grouped.identity.push(doc);
                  else if (groups.vehicle.some(k => key.startsWith(k))) grouped.vehicle.push(doc);
                  else grouped.other.push(doc);
                });
                return (
                  <>
                    {grouped.identity.length > 0 && (
                      <div className="doc-group">
                        <h4 className="doc-group-title">Identity &amp; Licence</h4>
                        {grouped.identity.map(doc => (
                          <DocRow key={doc.id} label={doc.document_type.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())} path={doc.file_path} />
                        ))}
                      </div>
                    )}
                    {grouped.vehicle.length > 0 && (
                      <div className="doc-group">
                        <h4 className="doc-group-title">Vehicle</h4>
                        {grouped.vehicle.map(doc => (
                          <DocRow key={doc.id} label={doc.document_type.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())} path={doc.file_path} />
                        ))}
                      </div>
                    )}
                    {grouped.other.length > 0 && (
                      <div className="doc-group">
                        <h4 className="doc-group-title">Other</h4>
                        {grouped.other.map(doc => (
                          <DocRow key={doc.id} label={doc.document_type.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())} path={doc.file_path} />
                        ))}
                      </div>
                    )}
                  </>
                );
              })()
            ) : (
              <div className="doc-group">
                <h4 className="doc-group-title">Identity &amp; Licence</h4>
                <DocRow label="Driving Licence Front"   path={app.driving_licence_file} />
                <DocRow label="Passport / ID"           path={app.passport_file} />
                <DocRow label="DBS Barcode"             path={app.dbs_barcode_file} />
                <DocRow label="Green Badge"             path={app.green_badge_file} />
                <div className="doc-group">
                  <h4 className="doc-group-title">Vehicle</h4>
                  <DocRow label="Insurance Certificate" path={app.insurance_certificate_file} />
                  <DocRow label="MOT Certificate"       path={app.mot_certificate_file} />
                </div>
              </div>
            )}
          </div>

          <div className="detail-section">
            <h3>🚗 Vehicle</h3>
            <div className="detail-grid">
              <div className="detail-item"><label>Make / Model:</label><span>{app.vehicle_make} {app.vehicle_model}</span></div>
              <div className="detail-item"><label>Registration:</label><span>{app.vehicle_registration || '—'}</span></div>
              <div className="detail-item"><label>Year:</label><span>{app.vehicle_year || '—'}</span></div>
              <div className="detail-item"><label>Transmission:</label><span>{app.transmission_type || '—'}</span></div>
            </div>
          </div>

          <div className="detail-section">
            <h3>🏦 Bank Details</h3>
            <div className="detail-grid">
              <div className="detail-item"><label>Account Holder:</label><span>{app.bank_account_holder || '—'}</span></div>
              <div className="detail-item"><label>Bank:</label><span>{app.bank_name || '—'}</span></div>
              <div className="detail-item"><label>Sort Code:</label><span>{app.bank_sort_code && app.bank_sort_code !== 'NOT-PROVIDED' ? app.bank_sort_code : '—'}</span></div>
              <div className="detail-item"><label>Account Number:</label><span>{app.bank_account_number && app.bank_account_number !== 'NOT-PROVIDED' ? app.bank_account_number : '—'}</span></div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Update Application Status</h3>
            <form onSubmit={handleSubmit} className="status-form">
              <div className="form-group">
                <label>Status:</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="status-select"
                  disabled={updating}
                >
                  <option value="pending">Pending</option>
                  {app.status !== 'rejected' && <option value="approved">Approved</option>}
                  <option value="rejected">Rejected</option>
                </select>
                {app.status === 'rejected' && (
                  <p style={{ color: '#CF142B', fontSize: '0.82rem', marginTop: '0.35rem', fontWeight: 500 }}>
                    ⚠️ This application has been rejected. The instructor must submit a new application to be re-considered.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>Admin Notes (internal only):</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this application..."
                  className="admin-notes"
                  rows="3"
                  disabled={updating}
                />
              </div>

              {newStatus === 'rejected' && (
                <div className="form-group">
                  <label style={{ color: '#CF142B', fontWeight: 600 }}>Rejection Reason <span style={{ fontWeight: 400, fontSize: '0.85em' }}>(emailed to applicant)</span>:</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Explain why the application has been rejected so the applicant can reapply..."
                    className="admin-notes"
                    rows="4"
                    required
                    disabled={updating}
                    style={{ borderColor: '#CF142B' }}
                  />
                </div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={onClose} className="btn-cancel" disabled={updating}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={updating}>
                  {updating ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationsList;
